import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Gamepad2, Star, Clock, Users, GraduationCap, Layers, Trophy } from "lucide-react";

const LANGUAGE_DEPENDENCE_LABELS = [
  null,
  "No in-game text",
  "Some text needed",
  "Moderate text",
  "Extensive text",
  "Must know language",
];

function StatPill({ icon: Icon, label, value }) {
  if (value == null) return null;
  return (
    <div className="flex items-center gap-1.5 bg-muted rounded-lg px-3 py-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <div>
        <p className="text-[10px] text-muted-foreground leading-none mb-0.5">{label}</p>
        <p className="text-xs font-semibold text-foreground leading-none">{value}</p>
      </div>
    </div>
  );
}

export default function GameDetailModal({ game, open, onClose }) {
  if (!game) return null;

  const playerRange =
    game.min_players != null && game.max_players != null
      ? game.min_players === game.max_players
        ? `${game.min_players}`
        : `${game.min_players}–${game.max_players}`
      : null;

  const playtimeRange =
    game.min_playtime != null && game.max_playtime != null
      ? game.min_playtime === game.max_playtime
        ? `${game.min_playtime} min`
        : `${game.min_playtime}–${game.max_playtime} min`
      : null;

  const bestWith = (() => {
    const counts = game.best_with_counts;
    if (!counts?.length) return null;
    const lo = counts[0], hi = counts.at(-1);
    return lo === hi ? `${lo} players` : `${lo}–${hi} players`;
  })();

  const recPlayers =
    game.rec_min_players != null && game.rec_max_players != null
      ? game.rec_min_players === game.rec_max_players
        ? `${game.rec_min_players}`
        : `${game.rec_min_players}–${game.rec_max_players}`
      : null;

  const langLabel = game.language_dependence != null
    ? LANGUAGE_DEPENDENCE_LABELS[game.language_dependence] ?? null
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl leading-snug pr-6">{game.title}</DialogTitle>
          {game.year_published != null && (
            <p className="text-sm text-muted-foreground">{game.year_published}</p>
          )}
        </DialogHeader>

        <div className="flex gap-5 mt-1">
          {/* Cover image */}
          <div className="shrink-0">
            {game.image_url || game.thumbnail_url ? (
              <img
                src={game.image_url || game.thumbnail_url}
                alt={game.title}
                className="w-36 h-auto rounded-lg object-cover shadow-md"
              />
            ) : (
              <div className="w-36 h-48 rounded-lg bg-muted flex items-center justify-center">
                <Gamepad2 className="h-8 w-8 text-muted-foreground/40" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <StatPill icon={Users}        label="Players"       value={playerRange} />
              <StatPill icon={Clock}        label="Play time"     value={playtimeRange} />
              <StatPill icon={Star}         label="BGG Rating"    value={game.bgg_rating != null ? Number(game.bgg_rating).toFixed(1) : null} />
              <StatPill icon={Trophy}       label="BGG Rank"      value={game.bgg_rank != null ? `#${game.bgg_rank}` : null} />
              <StatPill icon={Layers}       label="Complexity"    value={game.weight != null ? `${Number(game.weight).toFixed(1)} / 5` : null} />
              <StatPill icon={Users}        label="Best with"     value={bestWith} />
              <StatPill icon={Users}        label="Recommended"   value={recPlayers != null ? `${recPlayers} players` : null} />
              <StatPill icon={GraduationCap} label="Min age"      value={game.min_age != null ? `${game.min_age}+` : null} />
              {langLabel && (
                <StatPill icon={GraduationCap} label="Language" value={langLabel} />
              )}
            </div>

            {/* Mechanics */}
            {game.mechanics?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Mechanics
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {game.mechanics.map((m) => (
                    <Badge key={m.id} variant="secondary" className="text-xs font-normal">
                      {m.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Categories */}
            {game.categories?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Categories
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {game.categories.map((c) => (
                    <Badge key={c.id} variant="outline" className="text-xs font-normal">
                      {c.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {game.description && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Description
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line line-clamp-[12]">
              {game.description}
            </p>
          </div>
        )}

        {/* BGG link */}
        <div className="mt-4 pt-3 border-t border-border">
          <a
            href={`https://boardgamegeek.com/boardgame/${game.bgg_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            View on BoardGameGeek →
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
